import { useMemo, useState } from 'react'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import { useStudioStore } from '@/store/studio-store'
import { BLOCK_VIEW_COPY } from './block-view-copy'
import { describeBlockActions } from './block-actions'
import {
  projectBlockLabel,
  projectBlockNames,
  projectBlockParts,
} from './project-block-parts'

function namesIn(value: unknown): string[] {
  return typeof value === 'object' && value !== null
    ? Object.keys(value as Record<string, unknown>)
    : []
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="mt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {heading}
      </h3>
      <div className="mt-1.5 text-sm text-foreground">{children}</div>
    </section>
  )
}

/**
 * What the creator's app is made of, from their own project.
 *
 * The six Cogo JSON files each answer one question, and the interesting answer
 * is the join between three of them: the element pressed, the event it raises,
 * and the flow that runs. Shown by the block's own `semanticRole` rather than
 * the element id, because the id is an address and the role is a name.
 */
export function BlockOntologyPanel() {
  const { locale } = useStudioLocale()
  const copy = BLOCK_VIEW_COPY[locale]
  const currentProject = useStudioStore((s) => s.currentProject)

  const blockNames = useMemo(() => projectBlockNames(currentProject), [currentProject])
  const [selected, setSelected] = useState<string | null>(null)
  const active = selected && blockNames.includes(selected) ? selected : blockNames[0]
  const parts = useMemo(
    () => (active ? projectBlockParts(currentProject, active) : null),
    [currentProject, active],
  )
  const actions = useMemo(() => (parts ? describeBlockActions(parts) : []), [parts])

  if (blockNames.length === 0 || !parts) {
    return (
      <div data-testid="block-ontology-panel" className="p-4 text-sm text-muted-foreground">
        {copy.empty}
      </div>
    )
  }

  const block = parts.block as Record<string, unknown> | undefined
  const sources = namesIn((parts.datasource as Record<string, unknown> | undefined)?.sources)
  const variables = namesIn((parts.variables as Record<string, unknown> | undefined)?.variables)

  return (
    <div data-testid="block-ontology-panel" className="flex gap-4 p-4">
      <nav className="w-48 shrink-0">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {copy.blocksHeading}
        </h2>
        <ul className="mt-2 flex flex-col gap-1">
          {blockNames.map((name) => (
            <li key={name}>
              <button
                type="button"
                onClick={() => setSelected(name)}
                aria-current={name === active ? 'true' : undefined}
                className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${
                  name === active
                    ? 'bg-muted font-semibold text-foreground'
                    : 'text-muted-foreground hover:bg-muted/60'
                }`}
              >
                {projectBlockLabel(currentProject, name)}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="min-w-0 flex-1">
        <h2 className="text-base font-semibold text-foreground">{copy.title}</h2>

        <Section heading={copy.whatItIs}>
          {[projectBlockLabel(currentProject, active), block?.type]
            .filter(Boolean)
            .join(' · ') || copy.nothingHere}
        </Section>

        <Section heading={copy.whenPressed}>
          {actions.length === 0 ? (
            copy.nothingHere
          ) : (
            <ul className="flex flex-col gap-1.5">
              {actions.map((action) => (
                <li key={`${action.elementId}:${action.event}`}>
                  <span className="font-medium">
                    {copy.pressLabel(
                      action.semanticRole ?? action.elementId,
                      action.event,
                    )}
                  </span>
                  {' → '}
                  {action.flow === null ? (
                    copy.nothingHere
                  ) : action.flowExists ? (
                    <span className="font-mono text-[13px]">{action.flow}</span>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-400">
                      <span className="font-mono text-[13px]">{action.flow}</span> —{' '}
                      {copy.runsNothing}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section heading={copy.usesData}>
          {sources.length === 0 ? copy.nothingHere : sources.join(', ')}
        </Section>

        <Section heading={copy.remembers}>
          {variables.length === 0 ? copy.nothingHere : variables.join(', ')}
        </Section>
      </div>
    </div>
  )
}
